<template>
  <v-auth-layout>
    <div id="login-container">
      <div class="login-header mb-32">
        <h1 class="login-title h4 fw-bold text-dark mb-8 letter-spacing-tight">
          {{ $t("auth.login.title") }}
        </h1>
        <p class="login-subtitle text-muted small mb-0">
          {{ $t("auth.login.subtitle") }}
        </p>
      </div>

      <div v-if="socialLoginBtnLoading" class="position-relative py-40">
        <v-loader/>
      </div>

      <template v-else>
        <div class="social-logins d-flex flex-column gap-12 mb-24">
          <!-- google login -->
          <v-button
            class="social-login-btn justify-content-center w-100 py-12"
            variant="outline-gray"
            @click="socialLogin(googleProvider)"
          >
            <Transition mode="out-in" name="fade">
              <div v-if="statusSocialLogin === 'loading'">
                <span class="spinner-border spinner-border-sm me-12" role="status" aria-hidden="true"></span>
                {{ $t('auth.login.redirect') }}
              </div>

              <div v-else class="d-flex align-items-center">
                <img
                  :alt="$t('auth.login.logInGoogle')"
                  src="@/../resources/img/google-logo.svg"
                  class="social-icon me-16"
                />
                <span class="social-login-label fw-bold text-dark text-sm">{{ $t('auth.login.logInGoogle') }}</span>
              </div>
            </Transition>
          </v-button>

          <!-- OR separator -->
          <div class="or-separator d-flex align-items-center text-center">
            <span class="text-muted xx-small fw-bold px-12">OR</span>
          </div>

          <!-- apple login -->
          <v-button
            class="social-login-btn justify-content-center w-100 py-12"
            variant="outline-gray"
            @click="socialLogin(appleProvider)"
          >
            <Transition mode="out-in" name="fade">
              <div v-if="statusSocialAppleLogin === 'loading'">
                <span class="spinner-border spinner-border-sm me-12" role="status" aria-hidden="true"></span>
                {{ $t("auth.login.redirect") }}
              </div>
              <div v-else class="d-flex align-items-center">
                <img
                  :alt="$t('auth.login.logInApple')"
                  src="@/../resources/img/apple-logo.svg"
                  class="social-icon me-16"
                />
                <span class="social-login-label fw-bold text-dark text-sm">{{ $t('auth.login.logInApple') }}</span>
              </div>
            </Transition>
          </v-button>
        </div>

        <!-- Only show email login in development mode -->
        <template v-if="isDevelopment">
          <div class="separator d-flex align-items-center text-center mb-24">
            <span class="text-muted xx-small text-uppercase fw-bold letter-spacing-wide px-16">
              {{ $t('auth.login.orContinueWithEmail') }}
            </span>
          </div>

          <form id="login" class="login-email-form mb-0" @submit.prevent="login">
            <fieldset class="border-0 p-0 m-0">
              <legend class="visually-hidden">{{ $t('auth.login.loginData') }}</legend>

              <v-form-input
                id="login-email"
                v-model="email"
                :label="`${$t('auth.shared.email')}*`"
                :type="'email'"
                :yup-errors-variable="errors.email"
                autocomplete="email"
                form-type="outline"
                class="mb-16"
              />

              <v-input-password-outline
                id="login-password"
                v-model="password"
                :label="`${$t('auth.shared.password')}*`"
                :yup-errors-variable="errors.password"
                autocomplete="current-password"
                class="mb-24"
              >
                <template #btn-icon>
                  <v-icon icon="eye" />
                </template>
              </v-input-password-outline>
            </fieldset>

            <v-button
              :disabled="!meta.valid || accepted"
              :is-loading="accepted"
              class="login-submit-btn w-100 py-12 fw-bold shadow-sm hover-lift"
              type="submit"
              variant="primary"
            >
              {{ $t("auth.login.logIn") }}
            </v-button>
          </form>
        </template>
      </template>
    </div>
  </v-auth-layout>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import * as yup from "yup";
import { useField } from "vee-validate";
import { VFormInput, VInputPasswordOutline } from "@uniqoders/form";
import { useI18n } from "vue-i18n";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import { useAuthStore } from "@/stores/auth";
import useFormValidation from "@/helpers/form";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VAuthLayout from "@/components/layout/VAuthLayout.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import { consumeReturnPath, rememberReturnPath } from "@/helpers/returnPath";

export default defineComponent({
  name: "TheLogin",
  components: {
    VLoader,
    VAuthLayout,
    VIcon,
    VButton,
    VFormInput,
    VInputPasswordOutline,
  },
  setup() {
    const { t } = useI18n();
    const authStore = useAuthStore();

    const rules = yup.object({
      email: yup.string().email().required().label(t("general.shared.email")),
      password: yup.string().required().label(t("general.shared.password")),
    });

    const form = useFormValidation(rules);

    const { value: email } = useField("email");
    const { value: password } = useField("password");

    return {
      ...form,
      email,
      password,
      authStore,
    };
  },
  data() {
    return {
      statusSocialLogin: "loaded" as string,
      statusSocialAppleLogin: "loaded" as string,
      socialLoginBtnLoading: false as boolean,
      googleProvider: 'google' as const,
      appleProvider: 'apple' as const
    };
  },
  computed: {
    isDevelopment(): boolean {
      return import.meta.env.VITE_NODE_ENV === 'development';
    },
  },
  created() {
    this.confirmAuthSocial();
  },
  methods: {
    async login() {
      try {
        this.toggleAccepted();
        await api.auth.login({ email: this.email, password: this.password });
        await this.authStore.setAuthUser();
        await this.redirectToHome();
      } catch (e: any) {
        console.error(e);
        this.$toast.error(e.response.data.message);
      } finally {
        this.toggleAccepted();
      }
    },
    async socialLogin(provider: string) {
      try {
        localStorage.setItem('socialLoginProvider', provider);
        if (provider === this.googleProvider) {
          this.statusSocialLogin = 'loading';
        } else if (provider === this.appleProvider) {
          this.statusSocialAppleLogin = 'loading';
        }

        const isSupplier = this.$route.query.is_supplier === 'true' || 
                           this.$route.query.redirect?.toString().includes('supplier-portal') || 
                           false;

        rememberReturnPath(this.$route.query.redirect);

        const response = await api.auth.socialLogin(provider, isSupplier);
        if (response.status === 'success') {
          window.location.href = response.redirect_uri;
        }
      } catch (e: any) {
        console.error(e.response.data);
        this.$toast.error(e.response.data.message);
        await this.$router.push({ name: 'login' });
      } finally {
        if (provider === this.googleProvider) {
          this.statusSocialLogin = 'loaded';
        } else if (provider === this.appleProvider) {
          this.statusSocialAppleLogin = 'loaded';
        }
      }
    },
    async confirmAuthSocial() {
      const socialLoginProvider = localStorage.getItem('socialLoginProvider');
      if (socialLoginProvider === this.googleProvider) {
        const { state, code, scope, authuser, hd, prompt } = this.$router.currentRoute.value.query;
        if (!!state && !!code && !!scope && !!authuser && !!prompt) {
          this.socialLoginBtnLoading = true;
          try {
            await api.auth.confirmSocialAuth(this.googleProvider, { state, code, scope, authuser, hd, prompt });
            await this.authStore.setAuthUser();
            await this.redirectToHome();
          } catch (e) {
            this.socialLoginBtnLoading = false;
            await this.$router.push({ name: 'login' });
            this.$toast.error(this.$t('auth.login.userNotAllowed'));
          }
        }
      } else if (socialLoginProvider === this.appleProvider) {
        const { state, code, id_token } = this.$router.currentRoute.value.query;
        if (!!state && !!code && !!id_token) {
          this.socialLoginBtnLoading = true;
          try {
            await api.auth.confirmSocialAuth(this.appleProvider, { state, code, id_token });
            await this.authStore.setAuthUser();
            await this.redirectToHome();
          } catch (e) {
            this.socialLoginBtnLoading = false;
            await this.$router.push({ name: 'login' });
            this.$toast.error(this.$t('auth.login.userNotAllowed'));
          }
        }
      }
    },
    async redirectToHome() {
      const returnPath = consumeReturnPath(this.$route.query.redirect);

      if (returnPath) {
        await this.$router.push(returnPath);

        if (this.$route.name !== 'login') {
          return;
        }

        rememberReturnPath(returnPath);
      }

      const isSupplier = localStorage.getItem("isSupplier");
      if (isSupplier === "true") {
        await this.$router.push({ name: "supplierPortal.upcoming" });
        return;
      }
      const isCollaborator = localStorage.getItem("isCollaborator");
      if(isCollaborator === "true"){
        await this.$router.push({ name: "collaboratorDashboard" });
        return;
      }
      const isAdminOrSuperAdmin = this.authStore.user?.roles?.some((r: any) => r.name === 'admin' || r.name === 'super_admin');
      if (isAdminOrSuperAdmin) {
        await this.$router.push({ name: "collaboratorDashboard" });
        return;
      }

      await this.$router.push({ name: "case-v2.index" });
    },
  },
});
</script>

<style lang="scss" scoped>
#login-container {
  font-family: 'Montserrat', Arial, sans-serif;
}

.letter-spacing-tight { letter-spacing: -0.025em; }
.letter-spacing-wide { letter-spacing: 0.12em; }
.xx-small { font-size: 0.65rem; }
.text-sm { font-size: 0.875rem; }

.login-header {
  animation: cascadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.login-title {
  font-family: 'Montserrat', Arial, sans-serif !important;
  font-weight: 800 !important;
  font-size: 1.75rem !important;
  line-height: 1.2;
  color: #2d2d2d;
}

.login-subtitle {
  font-family: 'Manrope', Arial, sans-serif !important;
  font-weight: 400 !important;
  font-size: 0.95rem !important;
  line-height: 1.55;
  color: #6b6b6b;
}

.social-logins {
  animation: cascadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.12s both;
}

.social-login-btn {
  border: 1.5px solid #e5e5e5 !important;
  border-radius: 10px !important;
  background-color: #fff !important;
  font-family: 'Montserrat', Arial, sans-serif !important;
  transition: border-color 0.22s ease, background-color 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    background-color: #fff8f0 !important;
    border-color: #ff7900 !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 121, 0, 0.12);
  }

  &:focus-visible {
    outline: none;
    border-color: #ff7900 !important;
    box-shadow: 0 0 0 3px rgba(255, 121, 0, 0.25);
  }

  .social-icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .social-login-label {
    font-family: 'Montserrat', Arial, sans-serif !important;
    font-weight: 600 !important;
  }
}

.or-separator {
  margin: 8px 0;
  animation: cascadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.18s both;

  span {
    font-family: 'Manrope', Arial, sans-serif !important;
    letter-spacing: 0.1em;
    color: #8a8a8a;
  }

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #ececec;
  }
}

.separator {
  animation: cascadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.22s both;

  span {
    font-family: 'Manrope', Arial, sans-serif !important;
    color: #8a8a8a;
  }

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1.5px solid #ececec;
  }
}

.login-email-form {
  animation: cascadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.28s both;
}

.login-submit-btn {
  font-family: 'Montserrat', Arial, sans-serif !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em;
  border-radius: 10px !important;
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;

  &:hover:not(:disabled) {
    background: #2d2d2d !important;
    border-color: #2d2d2d !important;
  }

  &:disabled {
    opacity: 0.55;
  }
}

.hover-lift {
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18) !important;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes cascadeIn {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-header,
  .social-logins,
  .or-separator,
  .separator,
  .login-email-form {
    animation: none;
  }

  .social-login-btn:hover,
  .hover-lift:hover:not(:disabled) {
    transform: none;
  }
}
</style>
